import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, CheckCircle, Bell } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReminderModal from '@/components/ReminderModal';
import { HealthDisclaimer, EvidenceBadge } from '@/components/HealthDisclaimer';
import WomensHealthHub from '@/components/WomensHealthHub';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WomensHealth = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Default landing is the new structured hub. A specific section drills in
  // when explicitly requested via ?section=menstrual|hormonal|pregnancy|screening.
  const sectionParam = searchParams.get('section');

  const [activeSection, setActiveSection] = useState(sectionParam || 'hub');
  const [view, setView] = useState('input'); // input or results
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSuggestion, setReminderSuggestion] = useState('');

  const goToSection = (key) => {
    setActiveSection(key);
    setView('input');
    setSearchParams({ section: key });
  };

  const goToHub = () => {
    setActiveSection('hub');
    setView('input');
    setSearchParams({});
  };

  // Menstrual Health State
  const [menstrualData, setMenstrualData] = useState({
    cycleDay: '',
    mood: '',
    symptoms: [],
    notes: ''
  });
  
  // Preventive Screening State
  const [screeningData, setScreeningData] = useState({
    age: '',
    lastMammogram: '',
    lastPapSmear: ''
  });
  
  // Pregnancy Planning State
  const [pregnancyData, setPregnancyData] = useState({
    tryingToConceive: '',
    takingVitamins: '',
    regularCycle: ''
  });
  
  // Hormonal Health State
  const [hormonalData, setHormonalData] = useState({
    symptoms: [],
    cycleRegularity: ''
  });
  
  const [results, setResults] = useState(null);

  const symptomsList = ['Cramps', 'Low Energy', 'Mood Changes', 'Headache', 'Bloating', 'Breast Tenderness'];
  const hormonalSymptomsList = ['Fatigue', 'Irregular Cycle', 'Mood Swings', 'Hot Flashes', 'Sleep Issues', 'Weight Changes'];

  // Toggle symptoms for menstrual
  const toggleMenstrualSymptom = (symptom) => {
    if (menstrualData.symptoms.includes(symptom)) {
      setMenstrualData({
        ...menstrualData,
        symptoms: menstrualData.symptoms.filter(s => s !== symptom)
      });
    } else {
      setMenstrualData({
        ...menstrualData,
        symptoms: [...menstrualData.symptoms, symptom]
      });
    }
  };

  // Toggle symptoms for hormonal
  const toggleHormonalSymptom = (symptom) => {
    if (hormonalData.symptoms.includes(symptom)) {
      setHormonalData({
        ...hormonalData,
        symptoms: hormonalData.symptoms.filter(s => s !== symptom)
      });
    } else {
      setHormonalData({
        ...hormonalData,
        symptoms: [...hormonalData.symptoms, symptom]
      });
    }
  };

  // Handle Menstrual Health Submit
  const handleMenstrualSubmit = (e) => {
    e.preventDefault();
    if (!menstrualData.cycleDay || !menstrualData.mood) {
      toast.error('Please complete all required fields');
      return;
    }

    const cycleDay = parseInt(menstrualData.cycleDay);
    let phase = '';
    if (cycleDay <= 5) phase = 'Menstrual';
    else if (cycleDay <= 14) phase = 'Follicular';
    else if (cycleDay <= 17) phase = 'Ovulation';
    else phase = 'Luteal';

    const recommendations = [];
    if (menstrualData.mood === 'Low' || menstrualData.mood === 'Anxious') {
      recommendations.push('Practice gentle yoga or meditation for 10 minutes');
    }
    if (menstrualData.symptoms.includes('Cramps')) {
      recommendations.push('Use a heating pad and try ginger tea');
    }
    if (menstrualData.symptoms.includes('Low Energy')) {
      recommendations.push('Eat iron-rich foods like spinach and lentils');
    }

    setResults({
      phase,
      recommendations: recommendations.length > 0 ? recommendations : ['You\'re doing great! Keep tracking your cycle.'],
      nextSteps: ['Continue daily tracking', 'Stay hydrated (8 glasses/day)', 'Get 7-8 hours of sleep']
    });
    setView('results');
    toast.success('Guidance ready! 🌸');
  };

  // Handle Preventive Screening Submit
  const handleScreeningSubmit = (e) => {
    e.preventDefault();
    if (!screeningData.age) {
      toast.error('Please enter your age');
      return;
    }

    const age = parseInt(screeningData.age);
    const screenings = [];

    if (age >= 40) {
      screenings.push({
        test: 'Mammogram',
        frequency: age >= 50 ? 'Every 1-2 years' : 'Discuss with doctor',
        priority: 'High'
      });
    }
    if (age >= 21) {
      screenings.push({
        test: 'Pap Smear',
        frequency: age < 30 ? 'Every 3 years' : 'Every 3-5 years (with HPV test)',
        priority: 'High'
      });
    }
    if (age >= 30) {
      screenings.push({
        test: 'Blood Pressure',
        frequency: 'Annually',
        priority: 'Medium'
      });
    }
    if (age >= 45) {
      screenings.push({
        test: 'Cholesterol Check',
        frequency: 'Every 5 years',
        priority: 'Medium'
      });
    }
    if (age >= 50) {
      screenings.push({
        test: 'Bone Density (DEXA)',
        frequency: 'Every 2 years',
        priority: 'Medium'
      });
    }

    setResults({ screenings });
    setView('results');
    toast.success('Screening recommendations ready!');
  };

  // Handle Pregnancy Planning Submit
  const handlePregnancySubmit = (e) => {
    e.preventDefault();
    if (!pregnancyData.tryingToConceive) {
      toast.error('Please answer all questions');
      return;
    }

    const recommendations = [];
    
    if (pregnancyData.tryingToConceive === 'yes') {
      recommendations.push({
        category: 'Prenatal Vitamins',
        tip: 'Start taking folic acid (400mcg daily) now',
        action: 'Helps prevent neural tube defects'
      });
      recommendations.push({
        category: 'Track Ovulation',
        tip: 'Monitor your cycle to identify fertile windows',
        action: 'Use ovulation predictor kits or track basal body temperature'
      });
    }

    if (pregnancyData.takingVitamins !== 'yes') {
      recommendations.push({
        category: 'Nutrition',
        tip: 'Start prenatal vitamins with folic acid',
        action: 'Consult your doctor for the right supplement'
      });
    }

    if (pregnancyData.regularCycle !== 'yes') {
      recommendations.push({
        category: 'Cycle Health',
        tip: 'Track your cycle for 3 months',
        action: 'Speak with a healthcare provider if irregularities persist'
      });
    }

    recommendations.push({
      category: 'Lifestyle',
      tip: 'Maintain a healthy weight and exercise regularly',
      action: 'Avoid alcohol, smoking, and limit caffeine'
    });

    setResults({ recommendations });
    setView('results');
    toast.success('Planning guidance ready! 🌱');
  };

  // Handle Hormonal Health Submit
  const handleHormonalSubmit = (e) => {
    e.preventDefault();
    if (hormonalData.symptoms.length === 0) {
      toast.error('Please select at least one symptom or concern');
      return;
    }

    const recommendations = [];

    if (hormonalData.symptoms.includes('Irregular Cycle')) {
      recommendations.push({
        symptom: 'Irregular Cycle',
        advice: 'Track your cycle for 3 months and note any patterns',
        lifestyle: 'Manage stress, maintain healthy weight, regular exercise'
      });
    }

    if (hormonalData.symptoms.includes('Fatigue')) {
      recommendations.push({
        symptom: 'Fatigue',
        advice: 'Ensure 7-8 hours of sleep and check iron levels',
        lifestyle: 'Eat balanced meals, stay hydrated, moderate caffeine'
      });
    }

    if (hormonalData.symptoms.includes('Mood Swings')) {
      recommendations.push({
        symptom: 'Mood Swings',
        advice: 'Consider tracking symptoms with your cycle',
        lifestyle: 'Regular exercise, omega-3 rich foods, stress management'
      });
    }

    if (hormonalData.symptoms.includes('Hot Flashes')) {
      recommendations.push({
        symptom: 'Hot Flashes',
        advice: 'Layer clothing, keep cool drinks nearby',
        lifestyle: 'Avoid triggers (spicy foods, alcohol), practice deep breathing'
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        symptom: 'General Wellness',
        advice: 'Continue monitoring symptoms',
        lifestyle: 'Maintain healthy diet, exercise, and sleep routine'
      });
    }

    setResults({ recommendations, consult: hormonalData.symptoms.length >= 3 });
    setView('results');
    toast.success('Hormonal health guidance ready!');
  };

  const resetSection = () => {
    setView('input');
    setResults(null);
  };

  const handleSaveReminder = async (reminderData) => {
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      await axios.post(`${API}/health-reminders`, reminderData, {
        withCredentials: true,
      });
      toast.success('Reminder set! 🔔 Check "My Reminders" to manage it.');
      setShowReminderModal(false);
    } catch (error) {
      console.error('Error saving reminder:', error);
      toast.error('Failed to save reminder');
    }
  };

  const openReminderModal = (suggestion) => {
    setReminderSuggestion(suggestion);
    setShowReminderModal(true);
  };

  // Results View
  if (view === 'results' && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-100 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={goToHub} className="mb-4" data-testid="womens-health-results-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Women's Health
          </Button>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {activeSection === 'menstrual' && 'Menstrual Health Guidance'}
            {activeSection === 'screening' && 'Your Screening Recommendations'}
            {activeSection === 'pregnancy' && 'Pregnancy Planning Guide'}
            {activeSection === 'hormonal' && 'Hormonal Health Support'}
          </h1>

          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <div>
                  <h3 className="font-bold text-gray-900">Assessment Complete!</h3>
                  <p className="text-sm text-gray-600">Here are your personalized recommendations</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Menstrual Results */}
          {activeSection === 'menstrual' && results.phase && (
            <>
              <Card className="mb-6 bg-purple-50 border-purple-200">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-gray-900 mb-3">Current Cycle Phase: {results.phase}</h3>
                  <div className="space-y-2">
                    {results.recommendations.map((rec, idx) => (
                      <p key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-purple-600">•</span>
                        {rec}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Next Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.nextSteps.map((step, idx) => (
                      <p key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        {step}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Screening Results */}
          {activeSection === 'screening' && results.screenings && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Recommended Screenings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {results.screenings.map((screen, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-2 ${
                      screen.priority === 'High' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-gray-900">{screen.test}</h4>
                        <span className={`text-xs px-2 py-1 rounded ${
                          screen.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {screen.priority} Priority
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">Frequency: {screen.frequency}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pregnancy Planning Results */}
          {activeSection === 'pregnancy' && results.recommendations && (
            <div className="space-y-4 mb-6">
              {results.recommendations.map((rec, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle className="text-base">{rec.category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 mb-2"><strong>Tip:</strong> {rec.tip}</p>
                    <p className="text-sm text-gray-600">{rec.action}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Hormonal Health Results */}
          {activeSection === 'hormonal' && results.recommendations && (
            <>
              <div className="space-y-4 mb-6">
                {results.recommendations.map((rec, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle className="text-base">{rec.symptom}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700 mb-2"><strong>Advice:</strong> {rec.advice}</p>
                      <p className="text-sm text-gray-600"><strong>Lifestyle:</strong> {rec.lifestyle}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {results.consult && (
                <Card className="mb-6 bg-amber-50 border-amber-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-900">
                      <strong>Note:</strong> You have multiple symptoms. Consider consulting a healthcare provider for a comprehensive evaluation.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Evidence Badge & Disclaimer */}
          <div className="space-y-3 mb-6">
            <EvidenceBadge className="w-full justify-center" />
            <HealthDisclaimer variant="compact" />
          </div>

          <Card>
            <CardContent className="pt-6 space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                onClick={() => openReminderModal(
                  activeSection === 'menstrual' ? 'Next Cycle Tracking' :
                  activeSection === 'screening' ? 'Schedule Preventive Screening' :
                  activeSection === 'pregnancy' ? 'Prenatal Checkup Reminder' :
                  'Hormonal Health Check'
                )}
              >
                <Bell className="h-4 w-4 mr-2" />
                Set Health Reminder
              </Button>
              <Button className="w-full bg-pink-600 hover:bg-pink-700" onClick={resetSection}>
                Track Again
              </Button>
              <Button className="w-full" variant="outline" onClick={() => navigate('/family-health')}>
                Back to Family Health
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Reminder Modal */}
        <ReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          onSave={handleSaveReminder}
          module="women"
          suggestedTitle={reminderSuggestion}
        />
      </div>
    );
  }

  // Section selector — render the new structured hub. The hub itself handles
  // 'live' drill-ins (via goToSection) and inline 'Preview' / 'Coming soon'
  // states for sections that aren't fully built yet.
  if (activeSection === 'hub') {
    return <WomensHealthHub onSelectSection={goToSection} />;
  }

  // Input forms for each section
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-100 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={goToHub} className="mb-4" data-testid="womens-health-back-to-hub">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Women's Health
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {activeSection === 'menstrual' && 'Menstrual Health Tracker'}
            {activeSection === 'screening' && 'Preventive Screening Check'}
            {activeSection === 'pregnancy' && 'Pregnancy Planning'}
            {activeSection === 'hormonal' && 'Hormonal Health Check'}
          </h1>
          <p className="text-gray-600">
            {activeSection === 'menstrual' && 'Track your cycle and get personalized guidance'}
            {activeSection === 'screening' && 'Get age-appropriate screening recommendations'}
            {activeSection === 'pregnancy' && 'Start your pregnancy journey with guidance'}
            {activeSection === 'hormonal' && 'Check your hormonal balance and get tips'}
          </p>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeSection === 'menstrual' ? 'default' : 'outline'}
            onClick={() => goToSection('menstrual')}
            size="sm"
          >
            Menstrual
          </Button>
          <Button
            variant={activeSection === 'screening' ? 'default' : 'outline'}
            onClick={() => goToSection('screening')}
            size="sm"
          >
            Screening
          </Button>
          <Button
            variant={activeSection === 'pregnancy' ? 'default' : 'outline'}
            onClick={() => goToSection('pregnancy')}
            size="sm"
          >
            Pregnancy
          </Button>
          <Button
            variant={activeSection === 'hormonal' ? 'default' : 'outline'}
            onClick={() => goToSection('hormonal')}
            size="sm"
          >
            Hormonal
          </Button>
        </div>

        {/* Menstrual Health Form */}
        {activeSection === 'menstrual' && (
          <form onSubmit={handleMenstrualSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">What day of your cycle are you on?</CardTitle>
                <p className="text-sm text-gray-600">Day 1 = first day of period</p>
              </CardHeader>
              <CardContent>
                <Input
                  type="number"
                  min="1"
                  max="35"
                  value={menstrualData.cycleDay}
                  onChange={(e) => setMenstrualData({ ...menstrualData, cycleDay: e.target.value })}
                  className="text-lg font-semibold"
                  placeholder="Enter cycle day (1-35)"
                  required
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">How's your mood today?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Great', 'Good', 'Low', 'Anxious'].map(mood => (
                    <button
                      key={mood}
                      type="button"
                      onClick={() => setMenstrualData({ ...menstrualData, mood })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        menstrualData.mood === mood
                          ? 'border-pink-600 bg-pink-100 text-pink-900'
                          : 'border-gray-300 hover:border-pink-300'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Any symptoms? (Select all that apply)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {symptomsList.map(symptom => (
                    <button
                      key={symptom}
                      type="button"
                      onClick={() => toggleMenstrualSymptom(symptom)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        menstrualData.symptoms.includes(symptom)
                          ? 'border-pink-600 bg-pink-100 text-pink-900'
                          : 'border-gray-300 hover:border-pink-300'
                      }`}
                    >
                      {menstrualData.symptoms.includes(symptom) && <CheckCircle className="h-4 w-4 inline mr-1" />}
                      {symptom}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700 h-12 text-lg font-bold">
              Get Today's Guidance 🌸
            </Button>
          </form>
        )}

        {/* Preventive Screening Form */}
        {activeSection === 'screening' && (
          <form onSubmit={handleScreeningSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">What's your age?</CardTitle>
                <p className="text-sm text-gray-600">We'll recommend age-appropriate screenings</p>
              </CardHeader>
              <CardContent>
                <Input
                  type="number"
                  min="18"
                  max="100"
                  value={screeningData.age}
                  onChange={(e) => setScreeningData({ ...screeningData, age: e.target.value })}
                  className="text-lg font-semibold"
                  placeholder="Enter your age"
                  required
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">When was your last mammogram? (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="date"
                  value={screeningData.lastMammogram}
                  onChange={(e) => setScreeningData({ ...screeningData, lastMammogram: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">When was your last Pap smear? (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="date"
                  value={screeningData.lastPapSmear}
                  onChange={(e) => setScreeningData({ ...screeningData, lastPapSmear: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-bold">
              Check My Screening Needs 🔍
            </Button>
          </form>
        )}

        {/* Pregnancy Planning Form */}
        {activeSection === 'pregnancy' && (
          <form onSubmit={handlePregnancySubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Are you currently trying to conceive?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['yes', 'no'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, tryingToConceive: option })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all ${
                        pregnancyData.tryingToConceive === option
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {option === 'yes' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Are you taking prenatal vitamins?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['yes', 'no'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, takingVitamins: option })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all ${
                        pregnancyData.takingVitamins === option
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {option === 'yes' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Do you have a regular menstrual cycle?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['yes', 'no'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setPregnancyData({ ...pregnancyData, regularCycle: option })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all ${
                        pregnancyData.regularCycle === option
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {option === 'yes' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg font-bold">
              Start Planning 🌱
            </Button>
          </form>
        )}

        {/* Hormonal Health Form */}
        {activeSection === 'hormonal' && (
          <form onSubmit={handleHormonalSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select your symptoms or concerns</CardTitle>
                <p className="text-sm text-gray-600">Choose all that apply</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {hormonalSymptomsList.map(symptom => (
                    <button
                      key={symptom}
                      type="button"
                      onClick={() => toggleHormonalSymptom(symptom)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        hormonalData.symptoms.includes(symptom)
                          ? 'border-purple-600 bg-purple-100 text-purple-900'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {hormonalData.symptoms.includes(symptom) && <CheckCircle className="h-4 w-4 inline mr-1" />}
                      {symptom}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">How regular is your cycle?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Very Regular', 'Somewhat Regular', 'Irregular', 'Very Irregular'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setHormonalData({ ...hormonalData, cycleRegularity: option })}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        hormonalData.cycleRegularity === option
                          ? 'border-purple-600 bg-purple-100 text-purple-900'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg font-bold">
              Check Hormonal Health ✨
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default WomensHealth;
